import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { logoutUser } from "../../../firebase/utility";
import { setUser } from "../../../store/actions/actions";

const useIdleTimeout = (logoutTimeout = 2400000, warningTimeout = 1200000) => {
    const dispatch = useDispatch();
    const [showIdleModal, setShowIdleModal] = useState(false);
    const [remainingTime, setRemainingTime] = useState(0); // Remaining time in seconds
    const timerRef = useRef(null);
    const intervalRef = useRef(null);
    const isLoggingOutRef = useRef(false);

    const performLogout = useCallback(async () => {
        if (isLoggingOutRef.current) return;
        isLoggingOutRef.current = true;

        try {
            await logoutUser();
            dispatch(setUser({}));
            localStorage.removeItem("lastActivity");
            setShowIdleModal(false);
        } finally {
            isLoggingOutRef.current = false;
        }
    }, [dispatch]);

    const handleResume = useCallback(() => {
        setShowIdleModal(false);
        localStorage.setItem("lastActivity", Date.now().toString());
    }, []);

    useEffect(() => {
        const checkInactivity = () => {
            const lastActivity = localStorage.getItem("lastActivity");
            const now = Date.now();

            if (!lastActivity) {
                localStorage.setItem("lastActivity", now.toString());
                return;
            }

            const timeSinceLastActivity = now - parseInt(lastActivity, 10);

            if (timeSinceLastActivity >= logoutTimeout) {
                performLogout();
            } else if (timeSinceLastActivity >= warningTimeout) {
                setShowIdleModal(true);
                // Calculate remaining seconds
                const remaining = Math.max(0, Math.ceil((logoutTimeout - timeSinceLastActivity) / 1000));
                setRemainingTime(remaining);
            } else {
                setShowIdleModal(false);
            }
        };

        const handleActivity = () => {
            // Don't reset timer if idle modal is showing
            if (showIdleModal) return;

            const lastActivity = localStorage.getItem("lastActivity");
            const now = Date.now();
            
            // Prevent activity from keeping the session alive if they're already past the logout timeout
            if (lastActivity && (now - parseInt(lastActivity, 10)) >= logoutTimeout) {
                performLogout();
                return;
            }

            localStorage.setItem("lastActivity", now.toString());
            clearTimeout(timerRef.current);
            
            // Set timer to check activity when warning threshold is reached
            timerRef.current = setTimeout(() => {
                checkInactivity();
            }, warningTimeout);
        };

        // --- Wake-up detection ---
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                checkInactivity();
            }
        };

        const handleFocus = () => {
            checkInactivity();
        };

        // --- Initial checks on load ---
        checkInactivity();
        if (!showIdleModal) {
            handleActivity();
        }

        // Safety-net interval
        // Check every 1s when modal is shown to keep timer updated, else check every 10s
        const intervalTime = showIdleModal ? 1000 : 10000;
        intervalRef.current = setInterval(() => {
            checkInactivity();
        }, intervalTime);

        const activityEvents = ["mousemove", "keydown", "scroll", "click", "touchstart"];
        activityEvents.forEach((event) => {
            window.addEventListener(event, handleActivity);
        });
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);

        return () => {
            clearTimeout(timerRef.current);
            clearInterval(intervalRef.current);
            activityEvents.forEach((event) => {
                window.removeEventListener(event, handleActivity);
            });
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", handleFocus);
        };
    }, [dispatch, logoutTimeout, warningTimeout, showIdleModal, performLogout]);

    return { showIdleModal, remainingTime, handleResume, handleLogout: performLogout };
};

export default useIdleTimeout;
